import { useRef, useEffect, useCallback } from 'react';
import { isNative } from '../platform/NativeBridge';

/**
 * True persistent overlay using Document Picture-in-Picture API
 * Falls back to a popup window for browsers without PiP support
 */
export function useOverlayPersistence({ isOverlay, onRestore, isListening, isSpeaking, isProcessing }) {
  const pipWindowRef = useRef(null);
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const stateRef = useRef({ isListening, isSpeaking, isProcessing });

  useEffect(() => {
    stateRef.current = { isListening, isSpeaking, isProcessing };
  }, [isListening, isSpeaking, isProcessing]);

  const drawOrb = useCallback((canvas) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    const cx = W / 2, cy = H / 2;
    let time = 0;

    const draw = () => {
      time += 0.016;
      const s = stateRef.current;
      const intensity = s.isSpeaking ? 1.0 : s.isListening ? 0.8 : s.isProcessing ? 0.6 : 0.35;
      ctx.clearRect(0, 0, W, H);

      // Glow layers
      const g1 = ctx.createRadialGradient(cx, cy, 0, cx, cy, cx * 0.9);
      g1.addColorStop(0, `rgba(60,200,255,${0.2 * intensity})`);
      g1.addColorStop(0.4, `rgba(20,100,200,${0.1 * intensity})`);
      g1.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g1;
      ctx.beginPath(); ctx.arc(cx, cy, cx * 0.9, 0, Math.PI * 2); ctx.fill();

      // Ring
      ctx.strokeStyle = `rgba(60,180,255,${0.3 + Math.sin(time * 3) * 0.15 * intensity})`;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(cx, cy, cx * 0.55 + Math.sin(time * 2) * 3, 0, Math.PI * 2);
      ctx.stroke();

      // Particles
      for (let i = 0; i < 16; i++) {
        const a = (Math.PI * 2 * i) / 16 + time * 0.4;
        const r = cx * 0.3 + Math.sin(time + i) * cx * 0.12;
        const alpha = (0.3 + Math.sin(time * 2 + i) * 0.3) * intensity;
        ctx.fillStyle = `rgba(80,200,255,${alpha})`;
        ctx.beginPath();
        ctx.arc(cx + Math.cos(a) * r, cy + Math.sin(a) * r, 1.5 + Math.sin(time + i) * 0.5, 0, Math.PI * 2);
        ctx.fill();
      }

      // Tendrils
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI * 2 * i) / 6 + Math.sin(time * 0.5 + i) * 0.3;
        const len = cx * 0.4 * intensity * (0.7 + Math.sin(time + i) * 0.3);
        ctx.strokeStyle = `rgba(40,160,255,${0.15 * intensity})`;
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        for (let j = 1; j <= 4; j++) {
          const p = j / 4;
          const jx = Math.sin(time * 1.5 + j * 2 + i) * 8 * intensity;
          const jy = Math.cos(time * 1.2 + j * 2 + i) * 8 * intensity;
          ctx.lineTo(cx + Math.cos(angle) * len * p + jx, cy + Math.sin(angle) * len * p + jy);
        }
        ctx.stroke();
      }

      // Core
      const cp = 1 + Math.sin(time * 4) * 0.15;
      const cg = ctx.createRadialGradient(cx, cy, 0, cx, cy, 6 * cp);
      cg.addColorStop(0, `rgba(255,255,255,${0.4 * intensity})`);
      cg.addColorStop(0.5, `rgba(120,220,255,${0.2 * intensity})`);
      cg.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = cg;
      ctx.beginPath(); ctx.arc(cx, cy, 6 * cp, 0, Math.PI * 2); ctx.fill();

      // Outer sparks
      for (let i = 0; i < 5; i++) {
        const sa = Math.sin(time * 0.7 + i * 1.3) * Math.PI * 2;
        const sd = cx * 0.6 + Math.sin(time + i) * cx * 0.1;
        ctx.fillStyle = `rgba(100,220,255,${0.3 * intensity})`;
        ctx.beginPath();
        ctx.arc(cx + Math.cos(sa) * sd, cy + Math.sin(sa) * sd, 1, 0, Math.PI * 2);
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, []);

  const openOverlay = useCallback(async () => {
    // Try Document Picture-in-Picture API first (Chrome 116+)
    if ('documentPictureInPicture' in window) {
      try {
        const pip = await window.documentPictureInPicture.requestWindow({
          width: 150,
          height: 150,
        });

        pip.document.body.style.cssText = 'margin:0;padding:0;background:transparent;overflow:hidden;cursor:grab;';
        pip.document.documentElement.style.cssText = 'background:transparent;';

        const canvas = pip.document.createElement('canvas');
        canvas.width = 150;
        canvas.height = 150;
        canvas.style.cssText = 'width:100%;height:100%;';
        pip.document.body.appendChild(canvas);

        // Triple-click to restore
        let clicks = 0;
        let clickTimer = null;
        canvas.addEventListener('click', () => {
          clicks++;
          if (clickTimer) clearTimeout(clickTimer);
          if (clicks >= 3) {
            clicks = 0;
            pip.close();
            onRestore();
          } else {
            clickTimer = setTimeout(() => { clicks = 0; }, 500);
          }
        });

        pip.addEventListener('pagehide', () => {
          if (animRef.current) cancelAnimationFrame(animRef.current);
          onRestore();
        });

        drawOrb(canvas);
        pipWindowRef.current = pip;
        return true;
      } catch (e) {
        console.warn('Document PiP failed:', e);
      }
    }

    // O shell Android não abre popups de janela. A Home continua disponível no
    // app principal e a sobreposição retorna com segurança sem tentar criar
    // uma segunda janela de navegador.
    if (isNative) return false;

    // Fallback: popup window
    try {
      const popup = window.open('', 'athena_overlay',
        `width=160,height=160,left=${window.screen.width - 200},top=${window.screen.height - 200},toolbar=no,menubar=no,scrollbars=no,resizable=yes,status=no`
      );

      if (popup) {
        popup.document.write(`
          <!DOCTYPE html>
          <html><head><title>Athena</title>
          <style>*{margin:0;padding:0}body{background:#030308;overflow:hidden;cursor:grab}canvas{width:100%;height:100%}</style>
          </head><body><canvas id="c" width="160" height="160"></canvas></body></html>
        `);
        popup.document.close();

        const canvas = popup.document.getElementById('c');
        if (canvas) drawOrb(canvas);

        let clicks = 0;
        let clickTimer = null;
        popup.document.addEventListener('click', () => {
          clicks++;
          if (clickTimer) clearTimeout(clickTimer);
          if (clicks >= 3) {
            clicks = 0;
            popup.close();
            onRestore();
          } else {
            clickTimer = setTimeout(() => { clicks = 0; }, 500);
          }
        });

        popup.addEventListener('beforeunload', () => {
          if (animRef.current) cancelAnimationFrame(animRef.current);
        });

        const checkClosed = setInterval(() => {
          if (popup.closed) { clearInterval(checkClosed); onRestore(); }
        }, 500);

        pipWindowRef.current = popup;
        return true;
      }
    } catch (e) {
      console.warn('Popup fallback failed:', e);
    }

    return false;
  }, [onRestore, drawOrb]);

  const closeOverlay = useCallback(() => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    if (pipWindowRef.current) {
      try { pipWindowRef.current.close(); } catch {}
      pipWindowRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (isOverlay) {
      openOverlay();
    } else {
      closeOverlay();
    }
    return () => closeOverlay();
  }, [isOverlay, openOverlay, closeOverlay]);

  return { closeOverlay };
}
